import React, {useEffect, useRef, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Stack} from '@mui/material';
import {Modal} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import usePurchaseProcessEndedEvent from '@contentlab/hooks/analytics/use-purchase-process-ended-event';
import usePurchaseProcessStartedEvent from '@contentlab/hooks/analytics/use-purchase-process-started-event';
import useViewDismissedEvent from '@contentlab/hooks/analytics/use-view-dismissed-event';
import useViewPresentedEvent from '@contentlab/hooks/analytics/use-view-presented-event';
import {contentLabActions, ErrorKind} from '@contentlab/stores/contentLabStore';
import freeTrialEndModalStore, {
  freeTrialEndModalActions,
} from '@contentlab/stores/freeTrialEndModalStore';
import {CONTENT_LAB_VIEW_NAMES} from '@/lib/analytics/analyticsConstants';
import reactQueryClient from '@/utils/reactQueryClient';
import {CONTENT_LAB, CURRENT_SUBSCRIPTION} from '@/hooks/content-studio/consts';
import useContentStudioChangeSubscriptionMutation from '@/hooks/content-studio/mutations/use-content-studio-change-subscription-mutation/useContentStudioChangeSubscription';
import useContentLabSubscriptionData from '@/hooks/use-content-lab-subscription-data';
import FreeTrialEndModalContent from './FreeTrialEndModalContent';

function FreeTrialEndModal() {
  const [isLoading, setIsLoading] = useState(false);
  const {
    isFreeTrialEndModalOpen,
    freeTrialEndModalReason,
    freeTrialEndModalSource,
    onTrialUpgradeSuccess,
  } = freeTrialEndModalStore(
    useShallow((state) => ({
      isFreeTrialEndModalOpen: state.isFreeTrialEndModalOpen,
      freeTrialEndModalReason: state.freeTrialEndModalReason,
      freeTrialEndModalSource: state.freeTrialEndModalSource,
      onTrialUpgradeSuccess: state.onTrialUpgradeSuccess,
    }))
  );

  const {viewPresented} = useViewPresentedEvent();
  const {viewDismissed} = useViewDismissedEvent();
  const {uiInteraction} = useUiInteractionEvent();
  const {purchaseProcessStarted} = usePurchaseProcessStartedEvent();
  const {purchaseProcessEnded} = usePurchaseProcessEndedEvent();
  const presentationIdRef = useRef<string>('');
  const purchaseProcessIdRef = useRef<string>('');

  const {activeSubscription, plansCollections} =
    useContentLabSubscriptionData();
  const {mutateAsync: changeSubscription} =
    useContentStudioChangeSubscriptionMutation();

  const selectedPlan = plansCollections
    .flatMap((collection) => collection.plans)
    .find(
      (plan) => plan.trialQuoteId === activeSubscription?.plan?.griffinQuoteId
    );
  const handleClose = (reason: string) => {
    const interactionId = uuid();
    uiInteraction({
      item_name: 'close_free_trial_end_modal',
      interaction_location: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
      interaction_id: interactionId,
    });
    viewDismissed({
      presentation_id: presentationIdRef.current,
      interaction_id: interactionId,
      reason,
    });
    purchaseProcessEnded({
      presentation_id: presentationIdRef.current,
      process_id: purchaseProcessIdRef.current,
      product_id: selectedPlan?.id ?? '',
      result: 'cancel',
      product_name: selectedPlan?.id ?? '',
    });
    freeTrialEndModalActions.closeAndResetFreeTrialEndModal();
  };

  const handleStartPaid = async () => {
    if (!activeSubscription?.id) {
      contentLabActions.addError({
        title: 'Unable to upgrade',
        description: 'Please try again later',
        kind: ErrorKind.FAILURE,
      });
      purchaseProcessEnded({
        presentation_id: presentationIdRef.current,
        process_id: purchaseProcessIdRef.current,
        product_id: '',
        result: 'failure',
        product_name: '',
      });
      return;
    }

    if (!selectedPlan) {
      contentLabActions.addError({
        title: 'Unable to upgrade',
        description: 'Please try again later',
        kind: ErrorKind.FAILURE,
      });
      purchaseProcessEnded({
        presentation_id: presentationIdRef.current,
        process_id: purchaseProcessIdRef.current,
        product_id: '',
        result: 'failure',
        product_name: '',
      });
      return;
    }
    setIsLoading(true);
    try {
      await changeSubscription({
        paymentSourceId: activeSubscription?.id,
        selectedPlanId: selectedPlan.id,
        isDryRun: false,
      });

      // Invalidate current subscription query to update CreditBox immediately
      reactQueryClient.invalidateQueries({
        queryKey: [CONTENT_LAB, CURRENT_SUBSCRIPTION],
      });

      purchaseProcessEnded({
        presentation_id: presentationIdRef.current,
        process_id: purchaseProcessIdRef.current,
        product_id: selectedPlan?.id ?? '',
        result: 'success',
        product_name: selectedPlan?.id ?? '',
      });
      if (onTrialUpgradeSuccess) {
        onTrialUpgradeSuccess();
      }
    } catch (error) {
      contentLabActions.addError({
        title: 'Unable to upgrade',
        description: 'Please try again later',
        kind: ErrorKind.FAILURE,
      });
      purchaseProcessEnded({
        presentation_id: presentationIdRef.current,
        process_id: purchaseProcessIdRef.current,
        product_id: '',
        result: 'failure',
        product_name: '',
      });
    } finally {
      setIsLoading(false);
    }
    freeTrialEndModalActions.closeAndResetFreeTrialEndModal();
  };

  useEffect(() => {
    if (isFreeTrialEndModalOpen) {
      const presentationId = uuid();
      const processId = uuid();
      presentationIdRef.current = presentationId;
      purchaseProcessIdRef.current = processId;
      purchaseProcessStarted({
        presentation_id: presentationId,
        process_id: processId,
        product_id: selectedPlan?.id ?? '',
        product_name: selectedPlan?.id ?? '',
      });
      viewPresented({
        presentation_id: presentationId,
        view_name: CONTENT_LAB_VIEW_NAMES.CREDIT_END_MODAL,
        view_type: 'popup',
        view_location: 'editor_generate',
        source: freeTrialEndModalSource,
      });
    }
    // It should not fire twice because both are beign set at the same time
  }, [
    isFreeTrialEndModalOpen,
    freeTrialEndModalSource,
    purchaseProcessStarted,
    selectedPlan?.id,
    viewPresented,
  ]);

  return (
    <Modal
      open={isFreeTrialEndModalOpen}
      testID="free-trial-end-modal"
      width={444}
      handleClose={() => handleClose('modal_closed')}
      fullScreen={false}
    >
      <Stack>
        <FreeTrialEndModalContent
          onBackToEditor={() => handleClose('back_to_editor_button_pressed')}
          onStartProSubscription={handleStartPaid}
          reason={freeTrialEndModalReason}
          isLoading={isLoading}
        />
      </Stack>
    </Modal>
  );
}

export default FreeTrialEndModal;
